import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Filter,
  Upload,
  RefreshCw,
  Download,
  X,
} from "lucide-react";
import {
  fetchQuestions,
  deleteQuestion,
  setFilters,
} from "../../redux/slices/questionSlice";
import BulkImportQuestionsDialog from "./BulkImportQuestionsDialog";
import {
  fetchCategories,
  selectFlatCategories,
} from "../../redux/slices/categorySlice";
import api from "../../services/api";
import { exportQuestionsCsv } from "../../lib/questions-csv";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useToast } from "../../components/ui/use-toast";
import QuestionDialog from "./QuestionDialog";

const Questions = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const {
    questions = [],
    loading,
    filters,
    pagination,
  } = useSelector((state) => state.question);
  const { categories = [] } = useSelector((state) => state.category);
  const flatCategories = useSelector(selectFlatCategories);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(filters.search || "");
  const [exporting, setExporting] = useState(false);
  const categoriesFetched = useRef(false);
  const searchDebounceRef = useRef(null);
  const skipSearchDebounce = useRef(false);

  useEffect(() => {
    if (!categoriesFetched.current) {
      categoriesFetched.current = true;
      dispatch(fetchCategories());
    }
  }, [dispatch]);

  useEffect(() => {
    const categoryFromUrl = searchParams.get("category_id");
    if (categoryFromUrl && categoryFromUrl !== filters.category_id) {
      dispatch(setFilters({ category_id: categoryFromUrl, page: 1 }));
    }
    const searchFromUrl = searchParams.get("search");
    if (searchFromUrl && searchFromUrl !== filters.search) {
      skipSearchDebounce.current = true;
      setSearchTerm(searchFromUrl);
      dispatch(setFilters({ search: searchFromUrl, page: 1 }));
    }
  }, [searchParams, dispatch, filters.category_id, filters.search]);

  useEffect(() => {
    const params = {
      page: filters.page || 1,
      limit: 20,
      ...(filters.category_id && { category_id: filters.category_id }),
      ...(filters.difficulty && { difficulty: filters.difficulty }),
      ...(filters.question_type && { question_type: filters.question_type }),
      ...(filters.search && { search: filters.search }),
    };
    dispatch(fetchQuestions(params));
  }, [
    dispatch,
    filters.difficulty,
    filters.question_type,
    filters.category_id,
    filters.search,
    filters.page,
  ]);

  // Debounced search as the user types (also runs on Enter via immediate apply)
  useEffect(() => {
    if (skipSearchDebounce.current) {
      skipSearchDebounce.current = false;
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const next = searchTerm.trim();
      const current = (filters.search || "").trim();
      if (next !== current) {
        dispatch(setFilters({ search: next, page: 1 }));
      }
    }, 350);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm, dispatch, filters.search]);

  const handleSearch = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    dispatch(setFilters({ search: searchTerm.trim(), page: 1 }));
  };

  const handleClearSearch = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    skipSearchDebounce.current = true;
    setSearchTerm("");
    dispatch(setFilters({ search: "", page: 1 }));
  };

  const handleFilterChange = (key, value) => {
    dispatch(setFilters({ [key]: value === "all" ? null : value, page: 1 }));
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
  };

  const handleDeleteClick = (question) => {
    setQuestionToDelete(question);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteQuestion(questionToDelete.id)).unwrap();
      toast({
        title: "Success",
        description: "Question deleted successfully",
      });
      setDeleteDialogOpen(false);
      setQuestionToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: error || "Failed to delete question",
        variant: "destructive",
      });
    }
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      easy: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      medium:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
      hard: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
      beginner: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
      intermediate:
        "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
      advanced:
        "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
    };
    return colors[difficulty] || "bg-gray-100 text-gray-800";
  };

  const getTypeLabel = (type) => {
    const labels = {
      mcq: "MCQ",
      multiple_select: "Multiple Select",
      true_false: "True/False",
    };
    return labels[type] || type;
  };

  const getCategoryName = (categoryId) => {
    const category = flatCategories.find((cat) => cat.id === categoryId);
    return category?.name?.en || category?.name || "N/A";
  };

  const buildFetchParams = () => ({
    page: filters.page || 1,
    limit: 20,
    ...(filters.category_id && { category_id: filters.category_id }),
    ...(filters.difficulty && { difficulty: filters.difficulty }),
    ...(filters.question_type && { question_type: filters.question_type }),
    ...(filters.search && { search: filters.search }),
  });

  const handleRefresh = () => {
    dispatch(fetchCategories());
    dispatch(fetchQuestions(buildFetchParams()));
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = {
        page: 1,
        limit: 1000,
        ...(filters.category_id && { category_id: filters.category_id }),
        ...(filters.difficulty && { difficulty: filters.difficulty }),
        ...(filters.question_type && { question_type: filters.question_type }),
        ...(filters.search && { search: filters.search }),
      };
      const response = await api.get("/admin/questions", { params });
      const list = Array.isArray(response.data?.data)
        ? response.data.data
        : [];
      if (!list.length) {
        toast({
          title: "Nothing to export",
          description: "No questions match the current filters.",
        });
        return;
      }
      exportQuestionsCsv(list, "questions-export.csv");
      toast({
        title: "Export ready",
        description: `Downloaded ${list.length} question(s) as CSV.`,
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description:
          error.response?.data?.message || "Could not export questions",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Question Bank</h1>
          <p className="text-muted-foreground mt-1">
            Manage your question repository
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={handleExportCsv}
            disabled={exporting || loading}
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          <Button variant="outline" onClick={() => setBulkImportOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Import CSV
          </Button>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Question
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh list
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2 flex gap-2">
              <div className="relative flex-1">
                <Input
                  placeholder="Search question text, options, tags…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
                {searchTerm ? (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              <Button onClick={handleSearch} variant="outline">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            {/* Category Filter */}
            <Select
              value={filters.category_id || "all"}
              onValueChange={(value) =>
                handleFilterChange("category_id", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {flatCategories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {"\u00A0".repeat((category.level || 0) * 2)}
                    {category.level > 0 ? "└ " : ""}
                    {category.name?.en || category.name || "Unnamed"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Difficulty Filter */}
            <Select
              value={filters.difficulty || "all"}
              onValueChange={(value) => handleFilterChange("difficulty", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Difficulties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Difficulties</SelectItem>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.question_type || "all"}
              onValueChange={(value) =>
                handleFilterChange("question_type", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="mcq">MCQ</SelectItem>
                <SelectItem value="multiple_select">Multiple Select</SelectItem>
                <SelectItem value="true_false">True/False</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Questions Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : !Array.isArray(questions) || questions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No questions found</p>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Create First Question
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Question</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Difficulty</TableHead>
                  <TableHead>Marks</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.isArray(questions) &&
                  questions.map((question) => (
                    <TableRow key={question.id}>
                      <TableCell className="font-medium max-w-md">
                        <div className="line-clamp-2">
                          {question.question_text?.en || "No text"}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getCategoryName(
                          question.category_id || question.category?.id
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {getTypeLabel(question.question_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={getDifficultyColor(question.difficulty)}
                        >
                          {question.difficulty}
                        </Badge>
                      </TableCell>
                      <TableCell>{question.default_marks || 5}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(Array.isArray(question.tags)
                            ? question.tags
                            : []
                          )
                            .filter(Boolean)
                            .slice(0, 2)
                            .map((tag, idx) => (
                              <Badge
                                key={idx}
                                variant="secondary"
                                className="text-xs"
                              >
                                {typeof tag === "string"
                                  ? tag
                                  : tag?.en || String(tag)}
                              </Badge>
                            ))}
                          {Array.isArray(question.tags) &&
                            question.tags.length > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{question.tags.length - 2}
                              </Badge>
                            )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(question)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteClick(question)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="flex justify-center gap-2 items-center">
          <Button
            variant="outline"
            disabled={pagination.page === 1}
            onClick={() => dispatch(setFilters({ page: pagination.page - 1 }))}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {pagination.page} of{" "}
            {pagination.total_pages ||
              Math.ceil(pagination.total / pagination.limit)}
          </span>
          <Button
            variant="outline"
            disabled={
              pagination.page >=
              (pagination.total_pages ||
                Math.ceil(pagination.total / pagination.limit))
            }
            onClick={() => dispatch(setFilters({ page: pagination.page + 1 }))}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <QuestionDialog
        open={isCreateDialogOpen || !!editingQuestion}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setEditingQuestion(null);
        }}
        onSaved={async () => {
          await dispatch(fetchQuestions(buildFetchParams()));
        }}
        question={editingQuestion}
        categories={categories}
      />

      <BulkImportQuestionsDialog
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        onSuccess={() => dispatch(fetchQuestions(buildFetchParams()))}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Question</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this question? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Questions;
